/**
 * Step 4 — VLANs & Management Network.
 * - 11 VLAN presets following the Third Octet Rule (172.16.<vlan>.0/24).
 * - Per-VLAN editor with collision-aware add (via wizardStore.addVlan).
 * - Management network attached to a LAG or standalone port.
 * - "Used ports" overview at the bottom.
 */
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    KeyRound, Users, Phone, Server, Printer, UsersRound, Cpu, Factory,
    Globe, Ban, Plus, ChevronDown, ChevronUp, Trash2, AlertTriangle, Network, Wifi,
} from "lucide-react";
import { toast } from "sonner";
import { useWizardStore } from "../state/wizardStore";
import { formatIPInput, validateIPv4 } from "../utils/validation";
import {
    calculateDHCPRange, COMMON_SUBNETS, getSubnetInfo, subnetsOverlap,
} from "../utils/networkUtils";

// Map preset id → Lucide icon + payload (id, default name, vlanId, third-octet-rule subnet, type)
const VLAN_PRESETS = [
    { id: "management", name: "Management", vlanId: "99", type: "management", icon: KeyRound,    accent: "#03D8F3" },
    { id: "staff",      name: "Staff",      vlanId: "10", type: "corporate",  icon: Users,        accent: "#03D8F3" },
    { id: "voip",       name: "VoIP",       vlanId: "20", type: "corporate",  icon: Phone,        accent: "#03D8F3" },
    { id: "servers",    name: "Servers",    vlanId: "30", type: "server",     icon: Server,       accent: "#00FF66" },
    { id: "printers",   name: "Printers",   vlanId: "40", type: "corporate",  icon: Printer,      accent: "#FFD100" },
    { id: "guest",      name: "Guest",      vlanId: "50", type: "guest",      icon: UsersRound,   accent: "#FFD100" },
    { id: "iot",        name: "IoT",        vlanId: "60", type: "iot",        icon: Cpu,          accent: "#FFD100" },
    { id: "ot",         name: "OT",         vlanId: "70", type: "iot",        icon: Factory,      accent: "#FFD100" },
    { id: "dmz",        name: "DMZ",        vlanId: "80", type: "server",     icon: Globe,        accent: "#EF4444" },
    { id: "blackhole",  name: "Blackhole",  vlanId: "777", type: "management", icon: Ban,         accent: "#EF4444", disableDhcp: true },
    { id: "custom",     name: "Custom",     vlanId: "",   type: "corporate",  icon: Plus,         accent: "#a1a1aa" },
];

const buildVlanFromPreset = (preset, existingIds) => {
    let id = preset.vlanId;
    if (!id || existingIds.has(id)) {
        // The wizardStore's addVlan will auto-bump anyway, but pre-bumping yields a nicer UX
        let n = preset.vlanId ? Number(preset.vlanId) : 100;
        while (existingIds.has(String(n))) n++;
        id = String(n);
    }
    const octet = id;
    const network_ip = `172.16.${octet}.0`;
    const gateway_ip = `172.16.${octet}.1`;
    const subnet_mask = "255.255.255.0";
    return {
        nome_vlan: preset.name,
        vlan_id: id,
        network_ip,
        gateway_ip,
        subnet_mask,
        enable_dhcp: !preset.disableDhcp,
        dhcp_start: `172.16.${octet}.100`,
        dhcp_end: `172.16.${octet}.200`,
        dns_primary: "8.8.8.8",
        dns_secondary: "1.1.1.1",
        porte_assegnate: [],
        vlan_type: preset.type,
    };
};

const VlanCard = ({ vlan, index, allVlans, allPorts, usedPortsByOther, onUpdate, onRemove, readOnly }) => {
    const [open, setOpen] = useState(false);
    const Icon = VLAN_PRESETS.find((p) => p.name === vlan.nome_vlan)?.icon || Network;
    const set = (patch) => !readOnly && onUpdate({ ...vlan, ...patch });

    const overlapsWith = useMemo(() => {
        return allVlans.find((v, i) =>
            i !== index &&
            validateIPv4(v.network_ip) && validateIPv4(vlan.network_ip) &&
            validateIPv4(v.subnet_mask) && validateIPv4(vlan.subnet_mask) &&
            subnetsOverlap(vlan.network_ip, vlan.subnet_mask, v.network_ip, v.subnet_mask)
        );
    }, [allVlans, index, vlan]);

    const dupeVid = useMemo(
        () => allVlans.some((v, i) => i !== index && String(v.vlan_id) === String(vlan.vlan_id)),
        [allVlans, index, vlan.vlan_id],
    );

    return (
        <div className={["rounded-lg border bg-surface/50 transition-colors",
            overlapsWith || dupeVid ? "border-accent-amber/40" : "border-white/[0.06]",
        ].join(" ")} data-testid={`vlan-card-${vlan.vlan_id}`}>
            <button type="button" onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-accent-cyan/10 border border-accent-cyan/30 text-accent-cyan shrink-0">
                    <Icon className="w-3.5 h-3.5" />
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-accent-cyan px-2 py-0.5 rounded-full bg-accent-cyan/10 border border-accent-cyan/30">
                    VID {vlan.vlan_id}
                </span>
                <span className="text-[13.5px] font-semibold text-text-primary truncate flex-1">{vlan.nome_vlan}</span>
                <span className="text-[10.5px] font-mono text-text-muted">{vlan.network_ip}/{validateIPv4(vlan.subnet_mask) ? maskToCidr(vlan.subnet_mask) : "?"}</span>
                <span className={["text-[10.5px] font-mono px-1.5 py-0.5 rounded",
                    vlan.enable_dhcp ? "bg-accent-mint/10 text-accent-mint" : "bg-white/[0.04] text-text-dim",
                ].join(" ")}>{vlan.enable_dhcp ? "DHCP" : "no DHCP"}</span>
                <span className="text-[10.5px] font-mono text-text-dim">{(vlan.porte_assegnate || []).length} ports</span>
                {(overlapsWith || dupeVid) && <AlertTriangle className="w-3.5 h-3.5 text-accent-amber" />}
                {open ? <ChevronUp className="w-4 h-4 text-text-dim" /> : <ChevronDown className="w-4 h-4 text-text-dim" />}
                <button type="button" disabled={readOnly} onClick={(e) => { e.stopPropagation(); onRemove(); }}
                    data-testid={`vlan-remove-${vlan.vlan_id}`}
                    className="text-text-dim hover:text-accent-red p-1 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="px-4 pb-4 space-y-4 pt-2 border-t border-white/[0.04]">
                            {dupeVid && <p className="text-[11px] text-accent-amber font-mono"><AlertTriangle className="inline w-3 h-3 mr-1" /> Duplicate VLAN ID with another VLAN.</p>}
                            {overlapsWith && (
                                <p className="text-[11px] text-accent-amber font-mono">
                                    <AlertTriangle className="inline w-3 h-3 mr-1" /> Overlaps with VLAN {overlapsWith.nome_vlan} ({overlapsWith.network_ip}/{maskToCidr(overlapsWith.subnet_mask)})
                                </p>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Name" testId={`vlan-${vlan.vlan_id}-name`} value={vlan.nome_vlan} onChange={(v) => set({ nome_vlan: v })} readOnly={readOnly} />
                                <Field label="VLAN ID" testId={`vlan-${vlan.vlan_id}-vid`} value={String(vlan.vlan_id)} onChange={(v) => set({ vlan_id: v.replace(/[^0-9]/g, "").slice(0, 4) })} mono readOnly={readOnly} />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <Field label="Network IP" testId={`vlan-${vlan.vlan_id}-net`} value={vlan.network_ip} onChange={(v) => set({ network_ip: formatIPInput(v) })} mono readOnly={readOnly} />
                                <Field label="Gateway IP" testId={`vlan-${vlan.vlan_id}-gw`} value={vlan.gateway_ip} onChange={(v) => set({ gateway_ip: formatIPInput(v) })} mono readOnly={readOnly} />
                                <div>
                                    <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Subnet mask</label>
                                    <select disabled={readOnly} value={vlan.subnet_mask} onChange={(e) => set({ subnet_mask: e.target.value })} data-testid={`vlan-${vlan.vlan_id}-mask`}
                                        className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan">
                                        {COMMON_SUBNETS.map((s) => <option key={s.mask} value={s.mask}>/{s.cidr} · {s.mask} · {s.hosts.toLocaleString()} hosts</option>)}
                                    </select>
                                </div>
                            </div>

                            <SubnetVisual ip={vlan.network_ip} mask={vlan.subnet_mask} />

                            {/* DHCP section */}
                            <div className="rounded-md border border-white/[0.04] bg-canvas/40 p-3 space-y-2">
                                <label className="inline-flex items-center gap-2 text-[12.5px] text-text-muted cursor-pointer">
                                    <input type="checkbox" disabled={readOnly} checked={vlan.enable_dhcp} onChange={(e) => {
                                        const enable = e.target.checked;
                                        const range = enable ? calculateDHCPRange(vlan.gateway_ip, vlan.subnet_mask, []) : { start: "", end: "" };
                                        set({ enable_dhcp: enable, dhcp_start: range.start || vlan.dhcp_start, dhcp_end: range.end || vlan.dhcp_end });
                                    }} className="accent-accent-cyan" data-testid={`vlan-${vlan.vlan_id}-dhcp`} />
                                    Enable DHCP server on this VLAN
                                </label>
                                {vlan.enable_dhcp && (
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-1">
                                        <Field label="Range start" testId={`vlan-${vlan.vlan_id}-dstart`} value={vlan.dhcp_start} onChange={(v) => set({ dhcp_start: formatIPInput(v) })} mono readOnly={readOnly} />
                                        <Field label="Range end"   testId={`vlan-${vlan.vlan_id}-dend`}   value={vlan.dhcp_end}   onChange={(v) => set({ dhcp_end: formatIPInput(v) })} mono readOnly={readOnly} />
                                        <Field label="DNS primary"   testId={`vlan-${vlan.vlan_id}-dns1`} value={vlan.dns_primary || ""} onChange={(v) => set({ dns_primary: formatIPInput(v) })} mono readOnly={readOnly} />
                                        <Field label="DNS secondary" testId={`vlan-${vlan.vlan_id}-dns2`} value={vlan.dns_secondary || ""} onChange={(v) => set({ dns_secondary: formatIPInput(v) })} mono readOnly={readOnly} />
                                    </div>
                                )}
                            </div>

                            {/* Port assignment */}
                            <div>
                                <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Assigned ports</label>
                                <div className="flex flex-wrap gap-1.5">
                                    {allPorts.map((p) => {
                                        const blocked = usedPortsByOther.has(p.name) && !(vlan.porte_assegnate || []).includes(p.name);
                                        const sel = (vlan.porte_assegnate || []).includes(p.name);
                                        return (
                                            <button key={p.name} type="button" disabled={readOnly || blocked}
                                                onClick={() => {
                                                    const cur = vlan.porte_assegnate || [];
                                                    set({ porte_assegnate: sel ? cur.filter((x) => x !== p.name) : [...cur, p.name] });
                                                }}
                                                data-testid={`vlan-${vlan.vlan_id}-port-${p.name}`}
                                                className={["px-2.5 py-1 rounded-md text-[11.5px] font-mono border transition-colors",
                                                    sel ? "bg-accent-cyan/20 border-accent-cyan/50 text-accent-cyan" :
                                                    blocked ? "bg-white/[0.02] border-white/[0.04] text-text-dim cursor-not-allowed line-through" :
                                                    "bg-canvas border-white/[0.08] text-text-muted hover:border-accent-cyan/30",
                                                ].join(" ")}>{p.name}</button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Type */}
                            <div>
                                <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Security tier</label>
                                <div className="flex flex-wrap gap-1 p-1 rounded-md bg-canvas border border-white/[0.06] w-fit">
                                    {["corporate", "guest", "server", "management", "iot"].map((t) => (
                                        <button key={t} type="button" disabled={readOnly} onClick={() => set({ vlan_type: t })}
                                            className={["px-3 py-1 rounded text-[11.5px] font-mono transition-colors",
                                                vlan.vlan_type === t ? "bg-accent-cyan text-canvas" : "text-text-muted hover:text-text-primary",
                                            ].join(" ")}>{t}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const Field = ({ label, value, onChange, placeholder, testId, mono, readOnly }) => (
    <div>
        <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">{label}</label>
        <input type="text" value={value || ""} readOnly={readOnly} placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className={["w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary placeholder:text-text-dim focus:outline-none focus:border-accent-cyan transition-colors",
                mono && "font-mono",
            ].filter(Boolean).join(" ")} />
    </div>
);

const SubnetVisual = ({ ip, mask }) => {
    const info = useMemo(() => {
        if (!validateIPv4(ip) || !validateIPv4(mask)) return null;
        try { return getSubnetInfo(ip, mask); } catch { return null; }
    }, [ip, mask]);
    if (!info) return null;
    return (
        <div className="rounded-md border border-white/[0.04] bg-canvas/40 p-3">
            <div className="flex items-stretch h-3 rounded overflow-hidden border border-white/[0.06]">
                <div className="bg-accent-cyan/40" style={{ width: "3%" }} title={`Network ${info.network}`} />
                <div className="bg-accent-mint/60" style={{ width: "94%" }} title={`Usable ${info.firstUsable}–${info.lastUsable}`} />
                <div className="bg-accent-amber/60" style={{ width: "3%" }} title={`Broadcast ${info.broadcast}`} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 text-[10.5px] font-mono text-text-dim">
                <span>Network: <span className="text-text-muted">{info.network}</span></span>
                <span>First: <span className="text-text-muted">{info.firstUsable}</span></span>
                <span>Last: <span className="text-text-muted">{info.lastUsable}</span></span>
                <span>Broadcast: <span className="text-text-muted">{info.broadcast}</span></span>
                <span>Hosts: <span className="text-text-muted">{info.usableHosts.toLocaleString()}</span></span>
                <span>/{info.cidr}</span>
            </div>
        </div>
    );
};

const maskToCidr = (m) => {
    if (!validateIPv4(m)) return "?";
    const o = m.split(".").map(Number);
    return o.reduce((a, x) => a + x.toString(2).replace(/0/g, "").length, 0);
};

const ManagementNetworkCard = ({ readOnly }) => {
    const { managementNetwork, setManagementNetwork, lacp_aggregates, mgmt_port, vlans, wan1, wan2 } = useWizardStore();
    const set = (patch) => !readOnly && setManagementNetwork(patch);

    const interfaceOptions = useMemo(() => {
        const opts = lacp_aggregates.map((a) => ({ value: a.name, label: `Aggregate ${a.name} (${(a.member_ports || []).length} ports)` }));
        // Allow standalone ports too (mgmt_port if set, fallback to "")
        if (mgmt_port) opts.push({ value: mgmt_port, label: `Port ${mgmt_port} (mgmt)` });
        return opts;
    }, [lacp_aggregates, mgmt_port]);

    const overlapWarning = useMemo(() => {
        if (!managementNetwork.enabled) return null;
        if (!validateIPv4(managementNetwork.ipAddress) || !validateIPv4(managementNetwork.subnetMask)) return null;
        for (const v of vlans) {
            if (validateIPv4(v.network_ip) && validateIPv4(v.subnet_mask) &&
                subnetsOverlap(managementNetwork.ipAddress, managementNetwork.subnetMask, v.network_ip, v.subnet_mask)) {
                return `Overlaps with VLAN ${v.nome_vlan} (${v.network_ip})`;
            }
        }
        for (const w of [wan1, wan2]) {
            if (w?.type === "static" && validateIPv4(w?.ip)) {
                if (subnetsOverlap(managementNetwork.ipAddress, managementNetwork.subnetMask, w.ip, w.subnet || "255.255.255.252")) {
                    return `Overlaps with WAN static IP ${w.ip}`;
                }
            }
        }
        return null;
    }, [managementNetwork, vlans, wan1, wan2]);

    return (
        <section className="rounded-xl border border-white/[0.06] bg-surface/60 backdrop-blur-md p-5" data-testid="mgmt-network">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-[15px] flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-accent-cyan" /> Management network
                </h3>
                <label className="inline-flex items-center gap-2 text-[12px] text-text-muted cursor-pointer">
                    <input type="checkbox" disabled={readOnly} checked={!!managementNetwork.enabled} onChange={(e) => set({ enabled: e.target.checked })} className="accent-accent-cyan" data-testid="mgmt-enabled" />
                    Enable
                </label>
            </div>
            {managementNetwork.enabled && (
                <div className="space-y-3">
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Attach to</label>
                        <select disabled={readOnly} value={managementNetwork.interface || ""} onChange={(e) => set({ interface: e.target.value })} data-testid="mgmt-interface"
                            className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan">
                            <option value="">Select interface…</option>
                            {interfaceOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="IP address" value={managementNetwork.ipAddress} onChange={(v) => set({ ipAddress: formatIPInput(v) })} mono testId="mgmt-ip" readOnly={readOnly} />
                        <Field label="Subnet mask" value={managementNetwork.subnetMask} onChange={(v) => set({ subnetMask: formatIPInput(v) })} mono testId="mgmt-mask" readOnly={readOnly} />
                    </div>
                    <SubnetVisual ip={managementNetwork.ipAddress} mask={managementNetwork.subnetMask} />
                    <label className="inline-flex items-center gap-2 text-[12.5px] text-text-muted cursor-pointer">
                        <input type="checkbox" disabled={readOnly} checked={!!managementNetwork.dhcpEnabled} onChange={(e) => set({ dhcpEnabled: e.target.checked })} className="accent-accent-cyan" />
                        Enable DHCP on management network
                    </label>
                    {managementNetwork.dhcpEnabled && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="DHCP start" value={managementNetwork.dhcpStart || ""} onChange={(v) => set({ dhcpStart: formatIPInput(v) })} mono testId="mgmt-dstart" readOnly={readOnly} />
                            <Field label="DHCP end" value={managementNetwork.dhcpEnd || ""} onChange={(v) => set({ dhcpEnd: formatIPInput(v) })} mono testId="mgmt-dend" readOnly={readOnly} />
                        </div>
                    )}
                    {overlapWarning && (
                        <p className="text-[11px] text-accent-amber font-mono"><AlertTriangle className="inline w-3 h-3 mr-1" />{overlapWarning}</p>
                    )}
                </div>
            )}
        </section>
    );
};

export const Step4VlansManagement = ({ hardwareData, readOnly = false }) => {
    const { vendor, modello, vlans, addVlan, updateStep4, wan1, wan2, mgmt_port, lacp_aggregates } = useWizardStore();

    const currentModel = useMemo(() => {
        const v = hardwareData?.vendors?.find((x) => x.vendorCode === vendor);
        return v?.models?.find((m) => m.name === modello) || null;
    }, [hardwareData, vendor, modello]);
    const allPorts = (currentModel?.ports || []).filter((p) => p.type !== "wan" && p.type !== "console" && p.type !== "ha");

    // Ports already taken by WAN, mgmt, LACP
    const otherUsed = useMemo(() => {
        const used = new Set();
        if (mgmt_port) used.add(mgmt_port);
        if (wan1.physical_port) used.add(wan1.physical_port);
        if (wan2.enabled && wan2.physical_port) used.add(wan2.physical_port);
        for (const a of lacp_aggregates) for (const p of (a.member_ports || [])) used.add(p);
        return used;
    }, [mgmt_port, wan1, wan2, lacp_aggregates]);

    const handleAddPreset = (preset) => {
        if (readOnly) return;
        const existingIds = new Set(vlans.map((v) => String(v.vlan_id)));
        const newVlan = buildVlanFromPreset(preset, existingIds);
        // Phase 5 polish: if the preset's default VLAN ID was already taken
        // and buildVlanFromPreset auto-bumped to the next free id, surface
        // a transient mint toast so the user knows what just happened.
        if (preset.vlanId && newVlan.vlan_id !== preset.vlanId) {
            toast.success(
                `VLAN ID ${preset.vlanId} was taken — auto-assigned ${newVlan.vlan_id}.`,
                { duration: 3000 },
            );
        }
        addVlan(newVlan);
    };

    const handleUpdate = (idx, next) => {
        const copy = [...vlans]; copy[idx] = next; updateStep4(copy);
    };
    const handleRemove = (idx) => {
        const copy = vlans.filter((_, i) => i !== idx); updateStep4(copy);
    };

    return (
        <div className="space-y-7" data-testid="step4-vlans-management">
            <header>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Step 4 of 7</p>
                <h2 className="mt-2 font-display font-semibold text-3xl tracking-tight">VLANs & management network</h2>
                <p className="mt-2 text-[13.5px] text-text-muted max-w-2xl">
                    Segment your network into VLANs using ready-made presets, then optionally pin a dedicated management network on a LAG or standalone port.
                </p>
            </header>

            {/* Preset strip */}
            <section data-testid="vlan-preset-strip">
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-2">Add from preset</p>
                <div className="flex flex-wrap gap-2 overflow-x-auto pb-2">
                    {VLAN_PRESETS.map((p) => {
                        const Icon = p.icon;
                        return (
                            <button key={p.id} type="button" disabled={readOnly} onClick={() => handleAddPreset(p)} data-testid={`preset-${p.id}`}
                                className="group inline-flex flex-col items-center gap-1.5 min-w-[88px] px-3 py-3 rounded-lg border border-white/[0.06] bg-surface/60 hover:border-accent-cyan/30 hover:bg-accent-cyan/[0.04] transition-colors">
                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border" style={{ background: `${p.accent}15`, borderColor: `${p.accent}40`, color: p.accent }}>
                                    <Icon className="w-4 h-4" />
                                </span>
                                <span className="text-[11.5px] font-medium text-text-primary">{p.name}</span>
                                <span className="text-[9.5px] font-mono text-text-dim">{p.vlanId || "VID"}</span>
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* VLAN list */}
            <section>
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-2">Configured VLANs ({vlans.length})</p>
                {vlans.length === 0 ? (
                    <div className="rounded-md border border-dashed border-white/[0.08] bg-canvas/30 p-7 text-center text-[12.5px] text-text-muted">
                        No VLANs yet — click a preset above to add one.
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        {vlans.map((v, i) => {
                            // ports used by OTHER vlans + base used set
                            const usedByOtherVlans = new Set(
                                vlans.filter((_, j) => j !== i).flatMap((x) => x.porte_assegnate || [])
                            );
                            const allBlocked = new Set([...usedByOtherVlans, ...otherUsed]);
                            return (
                                <VlanCard key={`${v.vlan_id}-${i}`} vlan={v} index={i}
                                    allVlans={vlans} allPorts={allPorts} usedPortsByOther={allBlocked}
                                    onUpdate={(next) => handleUpdate(i, next)} onRemove={() => handleRemove(i)} readOnly={readOnly} />
                            );
                        })}
                    </div>
                )}
            </section>

            {/* Management network */}
            <ManagementNetworkCard readOnly={readOnly} />

            {/* Used ports overview */}
            <section className="rounded-xl border border-white/[0.06] bg-surface/40 p-5" data-testid="used-ports-overview">
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-3">Port assignment overview</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
                    {(currentModel?.ports || []).map((p) => {
                        let role = "Free";
                        let tone = "bg-white/[0.03] text-text-dim border-white/[0.06]";
                        if (p.name === wan1.physical_port) { role = "WAN1"; tone = "bg-accent-cyan/10 text-accent-cyan border-accent-cyan/30"; }
                        else if (wan2.enabled && p.name === wan2.physical_port) { role = "WAN2"; tone = "bg-accent-cyan/10 text-accent-cyan border-accent-cyan/30"; }
                        else if (p.name === mgmt_port) { role = "MGMT"; tone = "bg-accent-mint/10 text-accent-mint border-accent-mint/30"; }
                        else {
                            for (const a of lacp_aggregates) {
                                if ((a.member_ports || []).includes(p.name)) { role = a.name; tone = "bg-accent-amber/10 text-accent-amber border-accent-amber/30"; break; }
                            }
                            if (role === "Free") {
                                for (const v of vlans) {
                                    if ((v.porte_assegnate || []).includes(p.name)) { role = v.nome_vlan; tone = "bg-accent-cyan/[0.04] text-text-primary border-accent-cyan/20"; break; }
                                }
                            }
                        }
                        return (
                            <div key={p.name} className={`px-2 py-2 rounded-md border text-center ${tone}`}>
                                <p className="text-[11px] font-mono text-text-primary">{p.name}</p>
                                <p className="text-[9.5px] mt-0.5 truncate">{role}</p>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};

export function isStep4Valid(state) {
    // Step 4 is "soft" — VLANs are optional. We only block on duplicate VLAN ids.
    const ids = (state.vlans || []).map((v) => String(v.vlan_id));
    return new Set(ids).size === ids.length;
}

export default Step4VlansManagement;
